import {
  TemporaryProcesses,
  WolfieConversation,
  WolfieFTUEStatus,
  WolfieKnowledge,
  WolfieMessage,
} from '@models/wolfie.model';
import { PersistPartial } from 'redux-persist/es/persistReducer';

export const WolfieTypes = {
  SET_WOLFIE_ENABLED: 'SET_WOLFIE_ENABLED',
  SET_WOLFIE_MINIMIZED: 'SET_WOLFIE_MINIMIZED',
  SET_WOLFIE_SHOW_ATTACH: 'SET_WOLFIE_SHOW_ATTACH',
  SET_WOLFIE_STATUS: 'SET_WOLFIE_STATUS',
  SET_WOLFIE_MESSAGES: 'SET_WOLFIE_MESSAGES',
  SET_WOLFIE_NEW_MESSAGES: 'SET_WOLFIE_NEW_MESSAGES',
  SET_WOLFIE_LOADING: 'SET_WOLFIE_LOADING',
  SET_WOLFIE_RATE_LIMITED: 'SET_WOLFIE_RATE_LIMITED',
  SET_WOLFIE_RATE_LIMIT_WARNING: 'SET_WOLFIE_RATE_LIMIT_WARNING',
  SET_WOLFIE_FTUE_STATUS: 'SET_WOLFIE_FTUE_STATUS',
  DISABLE_WOLFIE: 'DISABLE_WOLFIE',
  PERMANENTLY_DISABLE_WOLFIE: 'PERMANENTLY_DISABLE_WOLFIE',
  PERMANENTLY_REENABLE_WOLFIE: 'PERMANENTLY_REENABLE_WOLFIE',
  SET_WOLFIE_VIDEO_MODE: 'SET_WOLFIE_VIDEO_MODE',
  SET_WOLFIE_SPEAKING: 'SET_WOLFIE_SPEAKING',
  SET_WOLFIE_SHOULD_REFRESH_DATA: 'SET_WOLFIE_SHOULD_REFRESH_DATA',
  SET_WOLFIE_REFRESH_SEPARATION: 'SET_WOLFIE_REFRESH_SEPARATION',
  SET_WOLFIE_AUTO_ENABLE_MIC: 'SET_WOLFIE_AUTO_ENABLE_MIC',
  SET_WOLFIE_WAKE_WORD_ENABLED: 'SET_WOLFIE_WAKE_WORD_ENABLED',
  SET_WOLFIE_DEV_PANEL_ENABLED: 'SET_WOLFIE_DEV_PANEL_ENABLED',
  SET_WOLFIE_KNOWLEDGE: 'SET_WOLFIE_KNOWLEDGE',
  SET_WOLFIE_TEMPORARY_PROCESSES: 'SET_WOLFIE_TEMPORARY_PROCESSES',
  SET_WOLFIE_CONVERSATIONS: 'SET_WOLFIE_CONVERSATIONS',
  RESTART_WOLFIE: 'RESTART_WOLFIE',
  SEND_QUICK_COMMAND: 'SEND_QUICK_COMMAND',
};

export const setWolfieEnabled = (enabled: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_ENABLED,
  data: enabled,
});

export const setWolfieMinimized = (minimized: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_MINIMIZED,
  data: minimized,
});

export const setWolfieShowAttach = (showAttach: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_SHOW_ATTACH,
  data: showAttach,
});

export const setWolfieMessages = (messages: any[]) => ({
  type: WolfieTypes.SET_WOLFIE_MESSAGES,
  data: messages,
});

export const setWolfieNewMessages = (messages: any[]) => ({
  type: WolfieTypes.SET_WOLFIE_NEW_MESSAGES,
  data: messages,
});

export const setWolfieLoading = (loading: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_LOADING,
  data: loading,
});

export const setWolfieRateLimited = (rateLimited: boolean, rateLimitResetTime?: string) => ({
  type: WolfieTypes.SET_WOLFIE_RATE_LIMITED,
  data: { rateLimited, rateLimitResetTime },
});

export const setWolfieRateLimitWarning = (warning: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_RATE_LIMIT_WARNING,
  data: warning,
});

export const setWolfieFTUEStatus = (statusFTUE: WolfieFTUEStatus | null) => ({
  type: WolfieTypes.SET_WOLFIE_FTUE_STATUS,
  data: statusFTUE,
});

export const disableWolfie = () => ({
  type: WolfieTypes.DISABLE_WOLFIE,
  data: {},
});

export const permanentlyDisableWolfie = () => ({
  type: WolfieTypes.PERMANENTLY_DISABLE_WOLFIE,
  data: {},
});

export const permanentlyReenableWolfie = () => ({
  type: WolfieTypes.PERMANENTLY_REENABLE_WOLFIE,
  data: {},
});

export const setWolfieVideoMode = (videoMode: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_VIDEO_MODE,
  data: videoMode,
});

export const setWolfieSpeaking = (speaking: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_SPEAKING,
  data: speaking,
});

export const setWolfieShouldRefreshData = (shouldRefreshData: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_SHOULD_REFRESH_DATA,
  data: shouldRefreshData,
});

export const setWolfieRefreshSeparation = (refreshSeparation: number) => ({
  type: WolfieTypes.SET_WOLFIE_REFRESH_SEPARATION,
  data: refreshSeparation,
});

export const setWolfieAutoEnableMic = (autoEnableMic: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_AUTO_ENABLE_MIC,
  data: autoEnableMic,
});

export const setWolfieWakeWordEnabled = (enabled: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_WAKE_WORD_ENABLED,
  data: enabled,
});

export const setWolfieDevPanelEnabled = (enabled: boolean) => ({
  type: WolfieTypes.SET_WOLFIE_DEV_PANEL_ENABLED,
  data: enabled,
});

export const setWolfieKnowledge = (knowledge: WolfieKnowledge[]) => ({
  type: WolfieTypes.SET_WOLFIE_KNOWLEDGE,
  data: knowledge,
});

export const setWolfieTemporaryProcesses = (temporaryProcesses: TemporaryProcesses[]) => ({
  type: WolfieTypes.SET_WOLFIE_TEMPORARY_PROCESSES,
  data: temporaryProcesses,
});

export const setWolfieConversations = (conversations: WolfieConversation[]) => ({
  type: WolfieTypes.SET_WOLFIE_CONVERSATIONS,
  data: conversations,
});

export const restartWolfie = () => ({
  type: WolfieTypes.RESTART_WOLFIE,
  data: {},
});

export const sendQuickCommand = (command: string) => ({
  type: WolfieTypes.SEND_QUICK_COMMAND,
  data: command,
});

export type WolfieState = {
  messages: WolfieMessage[];
  newMessages: WolfieMessage[];
  loading: boolean;
  rateLimited: boolean;
  rateLimitResetTime: string | null;
  rateLimitWarning: boolean;
  enabled: boolean;
  permanentlyDisabled: boolean;
  ftueStatus: WolfieFTUEStatus | null;
  minimized: boolean;
  showAttach: boolean;
  videoMode: boolean;
  speaking: boolean;
  shouldRefreshData: boolean;
  refreshSeparation: number;
  autoEnableMic: boolean;
  shouldEnableMic: boolean;
  wakeWordEnabled: boolean;
  devPanelEnabled: boolean;
  knowledge: WolfieKnowledge[];
  temporaryProcesses: TemporaryProcesses[];
  conversations: WolfieConversation[];
  quickCommand: string | null;
} & PersistPartial;

// INITIAL STATE
const wolfieInitialState: WolfieState & PersistPartial = {
  messages: [],
  newMessages: [],
  loading: false,
  rateLimited: false,
  rateLimitResetTime: null,
  rateLimitWarning: false,
  enabled: true,
  permanentlyDisabled: false,
  ftueStatus: null,
  minimized: true,
  showAttach: false,
  videoMode: false,
  speaking: false,
  shouldRefreshData: false,
  refreshSeparation: 0,
  autoEnableMic: false,
  shouldEnableMic: false,
  wakeWordEnabled: false,
  devPanelEnabled: false,
  knowledge: [],
  temporaryProcesses: [],
  conversations: [],
  quickCommand: null,
  _persist: {
    version: 0,
    rehydrated: false,
  },
};

export type WolfieAction = {
  type: string;
  data: any;
};

const wolfieReducer = (state = wolfieInitialState, action: WolfieAction) => {
  switch (action.type) {
    case WolfieTypes.SET_WOLFIE_ENABLED:
      return {
        ...state,
        enabled: !state.permanentlyDisabled && action.data,
      };

    case WolfieTypes.SET_WOLFIE_MINIMIZED:
      return {
        ...state,
        minimized: action.data,
      };

    case WolfieTypes.SET_WOLFIE_SHOW_ATTACH:
      return {
        ...state,
        showAttach: action.data,
      };

    case WolfieTypes.SET_WOLFIE_STATUS:
      return {
        ...state,
        status: action.data,
      };

    case WolfieTypes.SET_WOLFIE_MESSAGES:
      return {
        ...state,
        // TODO: This is a temporary fix to avoid the messages array from growing too much, we may want to implement a more nuanced approach
        messages: action.data.slice(-100),
      };

    case WolfieTypes.SET_WOLFIE_NEW_MESSAGES:
      return {
        ...state,
        newMessages: action.data,
      };

    case WolfieTypes.SET_WOLFIE_LOADING:
      return {
        ...state,
        loading: action.data,
      };

    case WolfieTypes.SET_WOLFIE_RATE_LIMITED:
      return {
        ...state,
        rateLimited: action.data.rateLimited,
        rateLimitResetTime: action.data.rateLimitResetTime || null,
      };

    case WolfieTypes.SET_WOLFIE_RATE_LIMIT_WARNING:
      return {
        ...state,
        rateLimitWarning: action.data,
      };

    case WolfieTypes.SET_WOLFIE_FTUE_STATUS:
      return {
        ...state,
        ftueStatus: action.data,
      };

    case WolfieTypes.DISABLE_WOLFIE:
      return {
        ...state,
        enabled: false,
        confirmedElements: [],
        data: null,
        messages: [],
        mode: null,
        loading: false,
      };
    case WolfieTypes.PERMANENTLY_DISABLE_WOLFIE:
      return {
        ...state,
        permanentlyDisabled: true,
        enabled: false,
        data: null,
        messages: [],
        mode: null,
        loading: false,
      };

    case WolfieTypes.PERMANENTLY_REENABLE_WOLFIE:
      return {
        ...state,
        permanentlyDisabled: false,
      };

    case WolfieTypes.SET_WOLFIE_VIDEO_MODE:
      return {
        ...state,
        videoMode: action.data,
      };

    case WolfieTypes.SET_WOLFIE_SPEAKING:
      return {
        ...state,
        speaking: action.data,
      };

    case WolfieTypes.SET_WOLFIE_SHOULD_REFRESH_DATA:
      return {
        ...state,
        shouldRefreshData: action.data,
      };

    case WolfieTypes.SET_WOLFIE_REFRESH_SEPARATION:
      return {
        ...state,
        refreshSeparation: action.data,
      };

    case WolfieTypes.SET_WOLFIE_AUTO_ENABLE_MIC:
      return {
        ...state,
        autoEnableMic: action.data,
        wakeWordEnabled: false,
      };

    case WolfieTypes.SET_WOLFIE_WAKE_WORD_ENABLED:
      return {
        ...state,
        wakeWordEnabled: action.data,
        autoEnableMic: false,
      };

    case WolfieTypes.SET_WOLFIE_DEV_PANEL_ENABLED:
      return {
        ...state,
        devPanelEnabled: action.data,
      };

    case WolfieTypes.SET_WOLFIE_KNOWLEDGE:
      return {
        ...state,
        knowledge: action.data,
      };

    case WolfieTypes.SET_WOLFIE_TEMPORARY_PROCESSES:
      return {
        ...state,
        temporaryProcesses: action.data,
      };

    case WolfieTypes.SET_WOLFIE_CONVERSATIONS:
      return {
        ...state,
        conversations: action.data,
      };

    case WolfieTypes.RESTART_WOLFIE:
      return {
        ...state,
        messages: [],
        newMessages: [],
        knowledge: [],
        temporaryProcesses: [],
        conversations: [],
        loading: false,
        rateLimited: false,
        rateLimitResetTime: null,
        rateLimitWarning: false,
      };

    case WolfieTypes.SEND_QUICK_COMMAND:
      return {
        ...state,
        quickCommand: action.data,
      };

    default:
      return state;
  }
};

export default wolfieReducer;
